import { Shirt, Footprints, CircleDot, Hand, Shield, ShoppingBag, Award, Triangle, GitBranch, Flag, Cone } from "lucide-react";

// Lucide icons per accessory sub-type — detected from product name
function pickAccessoryIcon(nameFr = "") {
  const n = nameFr.toLowerCase();
  if (n.includes("plot") || n.includes("cône") || n.includes("cone")) return Triangle;
  if (n.includes("échelle") || n.includes("agilité")) return GitBranch;
  if (n.includes("chasuble")) return Flag;
  if (n.includes("brassard")) return Award;
  if (n.includes("sifflet")) return CircleDot;
  if (n.includes("casquette") || n.includes("bonnet")) return Shield;
  if (n.includes("serviette")) return Flag;
  if (n.includes("pompe")) return Cone;
  return ShoppingBag;
}

// Lucide icons per category
const ICONS = {
  jerseys: Shirt,
  cleats: Footprints,
  shoes: Footprints,
  training: Award,
  balls: CircleDot,
  gloves: Hand,
  socks: Award,
  shinguards: Shield,
};

// Stable hash → palette for visual variety across products
const PALETTES = [
  ["#0b0b0b", "#1a1a1a", "#D4AF37"], // black + gold (default)
  ["#080808", "#221b08", "#F4D77A"], // very dark + light gold
  ["#10100a", "#1f1a08", "#E8C766"], // warm dark + gold
  ["#0a0a0a", "#181818", "#FFD86B"], // pure black + warm gold
  ["#0b0805", "#1e1408", "#D4AF37"], // brown-black + gold
];

const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
};

export default function ProductPlaceholder({ product, size = "card" }) {
  const Icon =
    product.category === "accessories"
      ? pickAccessoryIcon(product.name_fr)
      : (ICONS[product.category] || ShoppingBag);
  const palette = PALETTES[hash(product.slug || "x") % PALETTES.length];
  const [bg1, bg2, gold] = palette;
  const isLarge = size === "large";

  // Short label for top corner — category initial only
  const tag = product.category?.toUpperCase().slice(0, 3);
  const num = String(hash(product.slug || "x") % 999 + 1).padStart(3, "0");

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        background: `radial-gradient(120% 80% at 30% 20%, ${bg2} 0%, ${bg1} 70%)`,
      }}
    >
      {/* Faint grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `linear-gradient(${gold} 1px, transparent 1px), linear-gradient(90deg, ${gold} 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
        }}
      />
      {/* Diagonal gold ribbon */}
      <div
        className="absolute -right-10 -top-10 w-40 h-40 rotate-12 opacity-30"
        style={{
          background: `linear-gradient(135deg, ${gold}, transparent 60%)`,
          filter: "blur(20px)",
        }}
      />
      {/* Glow */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          width: isLarge ? "55%" : "62%",
          height: isLarge ? "55%" : "62%",
          background: `radial-gradient(circle, ${gold}30 0%, transparent 65%)`,
          filter: "blur(20px)",
        }}
      />

      {/* Top-right tag */}
      <div className="absolute top-3 right-3 text-[9px] tracking-[0.3em] uppercase z-10"
        style={{ color: `${gold}`, fontFamily: '"JetBrains Mono", monospace' }}>
        BZ · {num}
      </div>

      {/* Center icon */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
        <div
          className="relative"
          style={{ filter: `drop-shadow(0 8px 20px ${gold}55)` }}
        >
          <Icon
            className={isLarge ? "w-32 h-32" : "w-20 h-20"}
            strokeWidth={1.2}
            style={{ color: gold }}
          />
        </div>

        {/* Product name */}
        <p
          className="mt-4 text-center font-black uppercase tracking-wider px-2 line-clamp-2"
          style={{
            fontFamily: '"Bebas Neue", system-ui',
            fontSize: isLarge ? "1.5rem" : "0.95rem",
            color: gold,
            letterSpacing: "0.05em",
            textShadow: `0 2px 12px ${gold}40`,
          }}
        >
          {product.name_fr?.split(" ").slice(0, 4).join(" ") || "Braf Zak"}
        </p>

        {/* Subtitle: colorway */}
        <p
          className="mt-1 text-center text-white/45"
          style={{
            fontFamily: '"Oswald", system-ui',
            fontSize: isLarge ? "0.7rem" : "0.55rem",
            letterSpacing: "0.25em",
          }}
        >
          {tag}
        </p>
      </div>

      {/* Bottom-left signature */}
      <div className="absolute bottom-3 left-3 text-[8px] tracking-[0.4em] uppercase z-10 opacity-50"
        style={{ color: gold, fontFamily: '"JetBrains Mono", monospace' }}>
        BRAF ZAK
      </div>
    </div>
  );
}
